import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MapPin, RefreshCw, Calendar, ChevronLeft, ChevronRight, Flame, Package } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { addGoogleBaseLayer } from '../../utils/googleBaseLayer';
import './FleetHeatmapPage.css';

if (!window.L) window.L = L;
require('leaflet.heat');

const FleetHeatmapPage = () => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const heatLayerRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pointCount, setPointCount] = useState(0);
    const [totalOrders, setTotalOrders] = useState(0);

    const [viewMode, setViewMode] = useState('week');
    const [currentDate, setCurrentDate] = useState(new Date());

    const getPeriodDates = useCallback((date, mode) => {
        const d = new Date(date);
        if (mode === 'week') {
            const day = d.getDay();
            const diff = d.getDate() - day + (day === 0 ? -6 : 1);
            const start = new Date(d.setDate(diff));
            start.setHours(0, 0, 0, 0);
            const end = new Date(start);
            end.setDate(end.getDate() + 6);
            end.setHours(23, 59, 59, 999);
            return { start, end };
        }
        const start = new Date(d.getFullYear(), d.getMonth(), 1);
        const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
        return { start, end };
    }, []);

    const periodDates = useMemo(() => getPeriodDates(currentDate, viewMode), [currentDate, viewMode, getPeriodDates]);

    const formatPeriodLabel = () => {
        const { start, end } = periodDates;
        if (viewMode === 'week') {
            const startStr = start.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
            const endStr = end.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
            return `${startStr} - ${endStr}`;
        }
        return start.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
    };

    const navigatePeriod = (direction) => {
        const newDate = new Date(currentDate);
        if (viewMode === 'week') {
            newDate.setDate(newDate.getDate() + (direction * 7));
        } else {
            newDate.setMonth(newDate.getMonth() + direction);
        }
        setCurrentDate(newDate);
    };

    useEffect(() => {
        if (!mapRef.current || mapInstanceRef.current) return;

        const map = L.map(mapRef.current, {
            center: [39.9334, 32.8597],
            zoom: 6,
            zoomControl: true,
            attributionControl: false
        });

        addGoogleBaseLayer(map, { dark: true });

        mapInstanceRef.current = map;

        return () => {
            map.remove();
            mapInstanceRef.current = null;
        };
    }, []);

    const loadHeatmapData = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const { start, end } = periodDates;
            const params = new URLSearchParams({
                startDate: start.toISOString(),
                endDate: end.toISOString()
            });

            const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.HEATMAP, {
                startDate: start.toISOString(),
                endDate: end.toISOString()
            });

            const points = res.data?.data || res.data || [];
            const pointList = Array.isArray(points) ? points : [];

            setPointCount(pointList.length);
            setTotalOrders(pointList.reduce((sum, p) => sum + (p.weight || 0), 0));

            if (heatLayerRef.current && mapInstanceRef.current) {
                mapInstanceRef.current.removeLayer(heatLayerRef.current);
                heatLayerRef.current = null;
            }

            if (pointList.length > 0 && mapInstanceRef.current) {
                const heatData = pointList.map(p => [p.latitude, p.longitude, p.weight || 1]);
                const maxWeight = Math.max(...pointList.map(p => p.weight || 1));

                heatLayerRef.current = L.heatLayer(heatData, {
                    radius: 30,
                    blur: 20,
                    maxZoom: 17,
                    max: maxWeight || 1,
                    minOpacity: 0.4,
                    gradient: {
                        0.0: '#0d47a1',
                        0.2: '#1565c0',
                        0.4: '#42a5f5',
                        0.6: '#66bb6a',
                        0.8: '#ffa726',
                        1.0: '#ef5350'
                    }
                }).addTo(mapInstanceRef.current);

                if (pointList.length === 1) {
                    mapInstanceRef.current.setView([pointList[0].latitude, pointList[0].longitude], 13);
                } else {
                    const bounds = L.latLngBounds(pointList.map(p => [p.latitude, p.longitude]));
                    mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
                }
            }

            if (!res.success && pointList.length === 0) {
                setError(res.error || 'Veri yüklenemedi');
            }
        } catch (err) {
            setError('Bir hata oluştu');
            console.error('Heatmap error:', err);
        } finally {
            setLoading(false);
        }
    }, [periodDates]);

    useEffect(() => {
        loadHeatmapData();
    }, [loadHeatmapData]);

    return (
        <div className="fleet-heatmap-page">
            <div className="fhm-header">
                <div className="fhm-header-left">
                    <div className="fhm-icon">
                        <Flame size={20} />
                    </div>
                    <div className="fhm-title">
                        <h1>Teslimat Yoğunluk Haritası</h1>
                        <p>Filonuzun teslim ettiği siparişlerin dağılımı</p>
                    </div>
                </div>
                <div className="fhm-header-right">
                    <button className="btn btn-secondary" onClick={loadHeatmapData} disabled={loading}>
                        <RefreshCw size={16} className={loading ? 'spinning' : ''} />
                        Yenile
                    </button>
                </div>
            </div>

            <div className="fhm-controls">
                <div className="fhm-view-toggle">
                    <button className={viewMode === 'week' ? 'active' : ''} onClick={() => setViewMode('week')}>Haftalık</button>
                    <button className={viewMode === 'month' ? 'active' : ''} onClick={() => setViewMode('month')}>Aylık</button>
                </div>
                <div className="fhm-period-nav">
                    <button onClick={() => navigatePeriod(-1)}><ChevronLeft size={18} /></button>
                    <span className="fhm-period-label">{formatPeriodLabel()}</span>
                    <button onClick={() => navigatePeriod(1)}><ChevronRight size={18} /></button>
                    <button className="fhm-today-btn" onClick={() => setCurrentDate(new Date())}>
                        <Calendar size={14} />Bugün
                    </button>
                </div>
                <div className="fhm-stats">
                    <div className="fhm-stat">
                        <MapPin size={14} />
                        <span>{pointCount} bölge</span>
                    </div>
                    <div className="fhm-stat">
                        <Package size={14} />
                        <span>{totalOrders} sipariş</span>
                    </div>
                </div>
            </div>

            {error && (
                <div className="fhm-error">
                    {error}
                </div>
            )}

            <div className="fhm-map-container">
                {loading && (
                    <div className="fhm-map-loading">
                        <div className="fhm-spinner" />
                        <span>Yükleniyor...</span>
                    </div>
                )}
                <div ref={mapRef} className="fhm-map" />
                <div className="fhm-legend">
                    <span className="fhm-legend-title">Yoğunluk</span>
                    <div className="fhm-legend-bar" />
                    <div className="fhm-legend-labels">
                        <span>Düşük</span>
                        <span>Yüksek</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FleetHeatmapPage;
